import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BookOpen, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";

export function NotebooksList() {
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ["notebooks"],
    queryFn: () => api.listNotebooks(),
  });

  const create = useMutation({
    mutationFn: () => api.createNotebook({ title: title.trim(), scope: "personal" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notebooks"] });
      setCreating(false);
      setTitle("");
      toast.success("Notebook creado");
    },
    onError: (err) => toast.error(`No se pudo crear: ${err}`),
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Mis apuntes</h1>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus size={14} /> Crear notebook
        </Button>
      </header>

      {list.isLoading && (
        <div className="grid gap-3 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {list.data?.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center">
          <BookOpen size={28} className="mx-auto mb-2 text-(--text-tertiary)" />
          <p className="text-(--text-secondary)">Mis apuntes está vacío.</p>
          <Button variant="primary" size="sm" className="mt-3" onClick={() => setCreating(true)}>
            Crear primer notebook
          </Button>
        </div>
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {list.data?.items.map((nb) => (
          <li key={nb.id}>
            <Link
              to="/notebooks/$notebookId"
              params={{ notebookId: nb.id }}
              className="block rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4 transition hover:bg-(--bg-hover)"
            >
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-(--accent-500)" />
                <h3 className="font-semibold">{nb.title}</h3>
              </div>
              <p className="mt-1 text-xs text-(--text-tertiary)">
                {nb.scope} · actualizado {formatRelative(nb.updated_at)}
              </p>
              {nb.description && (
                <p className="mt-1 text-sm text-(--text-secondary) line-clamp-2">
                  {nb.description}
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear notebook</DialogTitle>
            <DialogDescription>
              Crea un espacio para guardar notas, fragmentos y documentos relacionados.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Título del notebook…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && title.trim()) create.mutate();
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={!title.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
